import { Component, Element, Event, EventEmitter, h, Host, Prop } from "@stencil/core";

import {
  StzhChipBlurEvent,
  StzhChipClickEvent,
  StzhChipFocusEvent,
  StzhChipRemoveEvent,
  StzhChipSize,
  StzhChipType,
  StzhChipVariant,
} from "../../index";

/**
 * @slot - Slot for label
 * @slot icon - Slot for icon
 */
@Component({
  tag: "stzh-chip",
  styleUrl: "stzh-chip.scss",
  scoped: true,
})
export class StzhChip {
  /** Label */
  @Prop() label: string = "";

  /** Icon (use instead of icon slot) */
  @Prop() icon: string;

  /** `href` if the chip should be used as link  */
  @Prop() href: string = "";

  /** Target if the button is used as link (if `href` used)  */
  @Prop() target: string = "";

  /** Whether chip is not interactive */
  @Prop({ reflect: true }) nonInteractive: boolean = false;

  /** Whether remove button should be shown */
  @Prop({ reflect: true }) removable: boolean = false;

  /** Whether inverted style is shown (only available for `tag` variant) */
  @Prop({ reflect: true }) inverted: boolean = false;

  /** Variant */
  @Prop({ reflect: true }) variant: StzhChipVariant = "tag";

  /** Size */
  @Prop({ reflect: true }) size: StzhChipSize = "default";

  /** Size variant (above small breakpoint) */
  @Prop({ reflect: true }) sizeSmall: StzhChipSize;

  /** Size variant (above medium breakpoint) */
  @Prop({ reflect: true }) sizeMedium: StzhChipSize;

  /** Size variant (above large breakpoint) */
  @Prop({ reflect: true }) sizeLarge: StzhChipSize;

  /** Size variant (above ultra breakpoint) */
  @Prop({ reflect: true }) sizeUltra: StzhChipSize;

  /** Type */
  @Prop({ reflect: true }) type: StzhChipType = "default";

  /** Counter */
  @Prop() counter: string = "";

  /** Label of remove button for screenreaders */
  @Prop() removeLabel: string = "";

  /** Active */
  @Prop({ reflect: true }) active: boolean = false;

  /** Disabled */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Tabindex of link/button */
  @Prop({ attribute: "a11y-tabindex" }) a11yTabindex: string;

  /** Accessible label for screen readers to replace visible text */
  @Prop({ attribute: "a11y-label" }) a11yLabel: string;

  /** Id for element which describes the link/button */
  @Prop({ attribute: "a11y-describedby" }) a11yDescribedby: string = "";

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the link/button element.
   * Default value will be taken from `label` prop or default slot.
   */
  @Prop() analyticsId: string;

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the remove button element.
   * Default value will be taken from `removeLabel` prop.
   */
  @Prop() removeAnalyticsId: string;

  @Element() element: HTMLStzhChipElement;

  /** Chip click event */
  @Event() stzhClick: EventEmitter<StzhChipClickEvent>;

  /** Chip remove event */
  @Event() stzhRemove: EventEmitter<StzhChipRemoveEvent>;

  /** Focus event */
  @Event() stzhFocus: EventEmitter<StzhChipFocusEvent>;

  /** Input blur event */
  @Event() stzhBlur: EventEmitter<StzhChipBlurEvent>;

  private chip: HTMLButtonElement | HTMLAnchorElement;
  private text: HTMLElement;
  private focusedByInput: boolean = false;

  componentDidRender() {
    requestAnimationFrame(() => {
      this.chip?.setAttribute("s-object-id", this.analyticsId || this.text.innerText);
    });
  }

  private renderInner(): DocumentFragment {
    return (
      <div class="stzh-chip__button-inner">
        <div class="stzh-chip__icon-wrapper">
          {this.icon || (typeof this.icon === "undefined" && this.variant === "tag" && this.type !== "default") ? (
            <stzh-icon
              class="stzh-chip__icon"
              name={
                this.icon ||
                (this.type === "info" && "info-help") ||
                (this.type === "warning" && "important-warning") ||
                (this.type === "error" && "warning") ||
                (this.type === "success" && "checkmark-circle")
              }
            ></stzh-icon>
          ) : (
            <slot name="icon"></slot>
          )}
        </div>

        <stzh-clamp lines={1} class="stzh-chip__label" ref={el => (this.text = el as HTMLElement)}>
          {this.label ? this.label : <slot></slot>}
          {this.counter && this.counter !== "undefined" && this.counter !== undefined && (
            <span class="stzh-chip__vhidden">&nbsp;({this.counter})</span>
          )}
        </stzh-clamp>

        {this.counter && this.counter !== "undefined" && this.counter !== undefined && (
          <div class="stzh-chip__counter" aria-hidden="true">
            {this.counter}
          </div>
        )}
      </div>
    );
  }

  private onRootFocus = () => {
    if (!this.focusedByInput) {
      this.chip.focus();
    }

    this.focusedByInput = false;
  };

  private onFocus = (event: FocusEvent) => {
    this.focusedByInput = true;

    const focusEvent = new FocusEvent("focus", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(focusEvent);
    this.stzhFocus.emit({
      component: "stzh-chip",
      originalEvent: event,
    });
  };

  private onBlur = (event: FocusEvent) => {
    const blurEvent = new FocusEvent("blur", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(blurEvent);
    this.stzhBlur.emit({
      component: "stzh-chip",
      originalEvent: event,
    });
  };

  private onClick = (event: MouseEvent) => {
    this.stzhClick.emit({
      component: "stzh-chip",
      originalEvent: event,
      href: this.href,
    });
  };

  private onRemoveClick = (event: MouseEvent) => {
    this.stzhRemove.emit({
      component: "stzh-chip",
      originalEvent: event,
    });
  };

  render() {
    const classes = {
      "stzh-chip": true,
      "stzh-chip--is-active": this.active,
      "stzh-chip--is-disabled": this.disabled,
      "stzh-chip--is-removable": this.removable,
      "stzh-chip--is-inverted": this.inverted,
      "stzh-chip--has-counter": !!this.counter,
      [`stzh-chip--type-${this.type}`]: !!this.type,
      [`stzh-chip--size-${this.size}`]: !!this.size,
      [`stzh-chip--${this.variant}`]: !!this.variant,
    };

    const Chip = this.nonInteractive ? "div" : this.href ? "a" : "button";

    return (
      <Host tabindex={this.disabled ? null : "-1"} onFocus={this.onRootFocus}>
        <div class={classes}>
          {this.removable && (
            <button
              class="stzh-chip__remove"
              onClick={this.onRemoveClick}
              disabled={this.disabled}
              aria-label={this.removeLabel}
              type="button"
              s-object-id={this.removeAnalyticsId || this.removeLabel}
            >
              <stzh-icon class="stzh-chip__remove-icon" name="close-big"></stzh-icon>
            </button>
          )}

          {this.href ? (
            <Chip
              ref={el => (this.chip = el as HTMLAnchorElement)}
              class={{
                "stzh-chip__button": true,
                "is-button": !this.nonInteractive && !this.href,
                "is-link": !this.nonInteractive && !!this.href,
              }}
              href={this.disabled ? null : this.href}
              target={this.target}
              tabindex={this.a11yTabindex}
              aria-label={this.a11yLabel || null}
              aria-describedby={this.a11yDescribedby || null}
              onClick={this.onClick}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
            >
              {this.renderInner()}
            </Chip>
          ) : (
            <Chip
              ref={el => (this.chip = el as HTMLButtonElement)}
              class={{
                "stzh-chip__button": true,
                "is-button": !this.nonInteractive && !this.href,
                "is-link": !this.nonInteractive && !!this.href,
              }}
              disabled={this.disabled}
              type="button"
              tabindex={this.a11yTabindex}
              aria-label={this.a11yLabel || null}
              aria-describedby={this.a11yDescribedby || null}
              onClick={this.onClick}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
            >
              {this.renderInner()}
            </Chip>
          )}
        </div>
      </Host>
    );
  }
}
